<template>
    <section>
        <div class="mx-10 tablet:container overflow-x-auto overflow-y-hidden" v-cloak>
            <div ref="wrapper" class="ml-64 mobile:ml-32 mobile:text-xs mt-12 min-w-[1000px] pb-10">
                <div class="relative">
                    <div :style="{left: detailStore.trackerPosition + 'px'}"
                         class="w-0.5 h-full absolute bg-barometer-orange z-20 print:hidden tablet:hidden"></div>
                    <div class="flex justify-between h-full w-full absolute text-sm">
                        <div v-for="year in years" class="text-center w-full relative odd:bg-white even:bg-grey-100">
                            <div class="break-words absolute p-2 top-[-30px] w-full text-xs">
                                <template v-if="showFullYears">{{year}}</template>
                                <template v-else>'{{ year.toString().substring(2,4) }}</template>
                            </div>
                        </div>
                    </div>

                    <div class="relative pt-16 ">
                        <h3 class="absolute w-64 mobile:w-32 mobile:text-xs mt-[-25px] -translate-x-full text-right px-2 font-medium">{{
                                translate('algemeen')
                            }}</h3>
                        <cw-track :label="translate('mijlpalen')" :events="model.milestones"
                                  :company-end-date="model.timeline.endDate" :timeline-start="timelineStart"
                                  :timeline-end="timelineEnd"></cw-track>
                        <template v-if="!hasMultipleNameLanguages">
                            <cw-track :label="translate('benaming')" :periods="namesSorted"
                                      :company-end-date="model.timeline.endDate"
                                      :timeline-start="timelineStart" :timeline-end="timelineEnd"></cw-track>
                        </template>
                        <template v-else>
                            <cw-track v-for="(namesByLanguage, lang) in languages" :key="'name'+lang"
                                      :label="translate('benaming') + ' ' + lang.toString().toUpperCase()"
                                      :company-end-date="model.timeline.endDate" :periods="namesByLanguage"
                                      :timeline-start="timelineStart" :timeline-end="timelineEnd"></cw-track>
                        </template>
                        <cw-track :label="translate('maatschappelijke_zetel')" :periods="model.addresses"
                                  :company-end-date="model.timeline.endDate" :timeline-start="timelineStart"
                                  :timeline-end="timelineEnd"></cw-track>
                        
                        <cw-track v-if="model.liableParties.length > 0" :label="translate('liable_company') + ' (403)'" :periods="model.liableParties" :company-end-date="model.timeline.endDate" :timeline-start="timelineStart"
                                  :timeline-end="timelineEnd"></cw-track>
                    </div>

                    <div class="relative pt-16" v-if="detailStore.isBelgian">
                        <h3 class="absolute w-52 mobile:w-32 mobile:text-xs mt-[-25px] -translate-x-full text-right px-2 font-medium">{{
                                translate('juridisch')
                            }}</h3>
                        <cw-track :company-end-date="model.timeline.endDate" :label="translate('rsz-dagvaarding')"
                                  :events="model.dagvaardingen" :timeline-start="timelineStart"
                                  :timeline-end="timelineEnd"></cw-track>
                        <cw-track :company-end-date="model.timeline.endDate" :label="translate('inhoudingsplicht')"
                                  :periods="model.inhoudingsplichtPeriodes" :timeline-start="timelineStart"
                                  :timeline-end="timelineEnd" :red="true"></cw-track>
                        <cw-track :company-end-date="model.timeline.endDate" :label="translate('gerechtelijkakkoord')"
                                  :periods="model.gerechtelijkAkkoordPeriodes" :timeline-start="timelineStart"
                                  :timeline-end="timelineEnd" :red="true"></cw-track>
                    </div>
                    <div v-else-if="model.beneficiaryParties.length > 0" class="relative pt-16">
                        <h3 class="absolute w-52 mobile:w-32 mobile:text-xs mt-[-25px] -translate-x-full text-right px-2 font-medium">{{translate('verantw_voor')}} (403)</h3>
                        <cw-track v-for="(party, i) in model.beneficiaryParties"
                                  :key="'bp_'+i"
                                  :label="party.name"
                                  :type="party.type"
                                  :link="party.link"
                                  :periods="party.periods"
                                  :company-end-date="model.timeline.endDate"
                                  :timeline-start="timelineStart"
                                  :timeline-end="timelineEnd">
                        </cw-track>
                    </div>

                    <div class="relative pt-16">
                        <h3 class="absolute w-52 mobile:w-32 mobile:text-xs -translate-x-full mt-[-25px] text-right px-2 font-medium">
                            {{ translate('mandaathouders') }}</h3>
                        <cw-track v-for="(mandateHolder, i) in nonHistoricMandateolders"
                                  :key="i"
                                  :label="mandateHolder.name"
                                  :type="mandateHolder.type"
                                  :link="mandateHolder.link"
                                  :periods="mandateHolder.periods"
                                  :company-end-date="model.timeline.endDate"
                                  :timeline-start="timelineStart"
                                  :timeline-end="timelineEnd">
                        </cw-track>
                    </div>

                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import {useDetailStore} from "../../store";
import {SubPage, TimelineModel} from "../../types";
import _, {Dictionary} from "lodash";
import {LocalizedValuePeriod, PeriodList} from "./types";
import CwTrack from "./track.vue";
import {defineComponent, nextTick} from 'vue';

export default defineComponent({
    name: "timeline-wrapper",
    components: { CwTrack},
    data() {
        return {
            detailStore: useDetailStore(),
            maxYears: 20,
            SubPage
        }
    },
    computed: {
        nonHistoricMandateolders(): PeriodList[] {
            return this.model.mandateHolders.filter(mh => mh.periods.some(period => period.endDate === 0 || period.endDate > this.timelineStart));
        },
        showFullYears(): boolean {
            return (this.detailStore.wrapperWidth / this.maxYears) > 50;
        },
        model(): TimelineModel {
            return this.detailStore.pageModel[SubPage.TIMELINE]!;
        },
        languages(): Dictionary<LocalizedValuePeriod[]> {
            const namesAppearingInTimeline: LocalizedValuePeriod[] = this.model.names
                .filter(n => n.endDate === 0 || n.endDate > this.model.timeline.startDate);

            return _.groupBy(namesAppearingInTimeline, 'language');
        },
        hasMultipleNameLanguages(): boolean {
            return Object.keys(this.languages).map(e => this.languages[e]).length > 1;
        },
        namesSorted(): LocalizedValuePeriod[] {
            return this.model.names.sort((a, b) => a.startDate - b.startDate);
        },
        years(): number[] {
            const startYear = parseInt(this.model.timeline.startDate.toString().substring(0, 4));
            const endYear = parseInt(this.model.timeline.endDate.toString().substring(0, 4));

            return _.takeRight(_.range(startYear, endYear + 1), this.maxYears);
        },
        companyEndYear(): string {
            return this.model.timeline.endDate.toString().substring(0, 4);
        },
        timelineStart(): number {
            const companyStartYear = this.model.timeline.startDate.toString().substring(0, 4);
            return Math.max(
                +(companyStartYear + '0101'),
                +((this.model.timeline.endDate - ((this.maxYears - 1) * 10000)).toString().substring(0, 4) + '0101')
            );
        },
        timelineEnd(): number {
            return parseInt(this.companyEndYear + '1231');
        },
    },
    methods: {
        mouseIsMoving(e: MouseEvent) {
            this.detailStore.trackerPosition = e.pageX - this.detailStore.offsetLeft < 0 ? 0 : e.pageX - this.detailStore.offsetLeft;
        },
    },
    mounted() {
        const wrapper = this.$refs.wrapper as HTMLElement;
        if(!wrapper)
            return;

        nextTick(() => {
            wrapper.addEventListener('mousemove', this.mouseIsMoving);
            this.detailStore.offsetLeft = wrapper.offsetLeft;
            this.detailStore.wrapperWidth = wrapper.offsetWidth;
        });

        window.addEventListener("resize", () => {
            this.detailStore.offsetLeft = wrapper.offsetLeft;
            this.detailStore.wrapperWidth = wrapper.offsetWidth;
        });
    },
    unmounted() {
        (this.$refs.wrapper as HTMLElement)?.removeEventListener('mousemove', this.mouseIsMoving);
    }
});
</script>

